import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  CalendarDays,
  Clock3,
  Grid3X3,
  Heart,
  Leaf,
  MapPin,
  Search,
  ShieldCheck,
  Star,
  TicketCheck,
  Users,
} from "lucide-react";
import "./International.css";
import {
  formatPrice,
  getInternationalPage,
  getVisibleInternationalActivities,
} from "./International";
import usePageMeta from "../../../../hooks/usePageMeta";

const ADMIN_ACTIVITIES_KEY = "mbt_admin_activities";

function readAdminActivities() {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_ACTIVITIES_KEY) || "[]");
  } catch {
    return [];
  }
}

function mapAdminActivity(activity, fallbackImage) {
  return {
    id: activity.id,
    title: activity.title,
    location: activity.location || activity.city,
    rating: Number(activity.rating || 0),
    reviews: Number(activity.reviews || 0),
    duration: activity.duration || "Flexible",
    image: activity.imageUrl || fallbackImage,
    price: Number(activity.price || 0),
    oldPrice: Number(activity.oldPrice || activity.price || 0),
    desc: activity.description || "Explore this handpicked activity with easy booking and trusted support.",
  };
}

export default function International() {
  usePageMeta(
  "International Activities & Tours – MakeBeeTrip",
  "Book international tours, attractions and adventure activities across top global destinations. Sightseeing and experiences worldwide with MakeBeeTrip.",
  "international activities, international tours, things to do worldwide, global attractions, overseas sightseeing, adventure activities abroad"
);
  const location = useLocation();
  const navigate = useNavigate();
  const pageData = useMemo(() => getInternationalPage(new URLSearchParams(location.search)), [location.search]);
  const { activePlace, filterGroups, places, sortOptions } = pageData;
  const [search, setSearch] = useState("");
  const [where, setWhere] = useState(activePlace.label);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [sort, setSort] = useState("");
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [adminActivities, setAdminActivities] = useState(readAdminActivities);

  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: "ease-out-cubic", offset: 50 });
    const timer = setTimeout(() => AOS.refresh(), 80);
    return () => clearTimeout(timer);
  }, []);

  const mergedActivities = useMemo(() => {
    const fallbackImage = pageData.activities[0]?.image;
    const savedForPlace = adminActivities
      .filter((activity) => activity.cityCode === activePlace.code || activity.city === activePlace.short)
      .map((activity) => mapAdminActivity(activity, fallbackImage));

    return [...savedForPlace, ...pageData.activities];
  }, [activePlace.code, activePlace.short, adminActivities, pageData.activities]);
  const activities = useMemo(
    () => getVisibleInternationalActivities(mergedActivities, { search, sort }),
    [mergedActivities, search, sort]
  );

  useEffect(() => {
    setWhere(activePlace.label);
  }, [activePlace.label]);

  useEffect(() => {
    const syncAdminActivities = () => setAdminActivities(readAdminActivities());
    window.addEventListener("storage", syncAdminActivities);
    window.addEventListener("focus", syncAdminActivities);
    return () => {
      window.removeEventListener("storage", syncAdminActivities);
      window.removeEventListener("focus", syncAdminActivities);
    };
  }, []);

  const handleReset = () => {
    setSearch("");
    setWhere(activePlace.label);
    setDate(new Date().toISOString().slice(0, 10));
    setSort("");
    setFilterResetKey((key) => key + 1);
  };

  const handlePlaceSelect = (place) => {
    setSearch("");
    setSort("");
    setFilterResetKey((key) => key + 1);
    navigate(`/international-activities?toCode=${place.code}&city=${encodeURIComponent(place.short)}`);
  };

  return (
    <main className="international-page">
      <section className="international-shell">
        <section className="international-hero-search">
          <div className="international-hero-content">
            <div className="international-title-block">
              <div>
                <h1>Activities <Star size={18} /></h1>
                <p>Handpicked experiences & activities for your journey</p>
              </div>
            </div>

            <div className="international-perks">
              {[
                ["Curated Experiences", "Handpicked just for you", ShieldCheck],
                ["Best Prices", "Guaranteed", TicketCheck],
                ["Easy Booking", "Hassle-free", CalendarDays],
                ["24/7 Support", "We're here for you", Users],
              ].map(([title, subtitle, Icon]) => (
                <div className="international-perk" key={title}>
                  <Icon size={14} />
                  <span>
                    <strong>{title}</strong>
                    <small>{subtitle}</small>
                  </span>
                </div>
              ))}
            </div>

            <form className="international-top-search" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
              <label className="international-top-field">
                <span className="international-top-icon"><MapPin size={17} /></span>
                <span>
                  <small>Where?</small>
                  <input
                    value={where}
                    onChange={(event) => setWhere(event.target.value)}
                    list="international-place-options"
                    autoComplete="off"
                    placeholder="Search for a place or activity"
                  />
                </span>
                <datalist id="international-place-options">
                  {places.map((place) => (
                    <option key={place.code} value={place.label} />
                  ))}
                </datalist>
              </label>
              <label className="international-top-field international-top-field--date">
                <span className="international-top-icon"><CalendarDays size={17} /></span>
                <span>
                  <small>When?</small>
                  <input type="date" value={date} onChange={(event) => setDate(event.target.value)} autoComplete="off" />
                </span>
              </label>
              <button type="submit" className="international-top-search-btn">
                <Search size={18} />
                Search
              </button>
            </form>

            <div className="international-categories" aria-label="Activity categories">
              <span className="international-categories-label"><Grid3X3 size={14} /> Categories</span>
              {[
                ["All Activities", Grid3X3],
                ["Sightseeing", MapPin],
                ["Adventure", Star],
                ["Food & Experiences", Leaf],
                ["Culture & Heritage", ShieldCheck],
                ["Transfers", TicketCheck],
              ].map(([label, Icon], index) => (
                <button key={label} type="button" className={index === 0 ? "is-active" : ""}>
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>

            <div className="international-benefits">
              {[
                ["Best Prices", "Guaranteed", ShieldCheck],
                ["Easy Booking", "Hassle-free", TicketCheck],
                ["24/7 Support", "We're here", Users],
                ["Secure Payments", "100% Safe", ShieldCheck],
              ].map(([title, subtitle, Icon]) => (
                <div className="international-benefit" key={title}>
                  <Icon size={16} />
                  <span>
                    <strong>{title}</strong>
                    <small>{subtitle}</small>
                  </span>
                </div>
              ))}
            </div>

            <aside className="international-quote-card mbt-animated-card" data-aos="fade-left" data-aos-delay="120">
              <strong>Discover unforgettable</strong>
              <span>experiences at every destination</span>
            </aside>
          </div>
        </section>

        <div className="international-breadcrumb">Home &gt; Activities &gt; <span>{activePlace.short} Activities</span></div>

        <div className="international-heading">
          <div>
            <h1>{activePlace.short} <span>Activities</span></h1>
            <p>Handpicked international experiences for your journey.</p>
          </div>
        </div>

        <form className="international-tabs" onSubmit={(event) => event.preventDefault()}>
          {places.map((place) => (
            <input
              className={`international-tab-submit${place.code === activePlace.code ? " is-active" : ""}`}
              key={place.code}
              type="submit"
              value={place.short}
              onClick={() => handlePlaceSelect(place)}
            />
          ))}
        </form>

        <div className="international-layout">
          <aside className={`international-sidebar mbt-animated-card${filtersOpen ? " is-open" : ""}`} key={filterResetKey} data-aos="fade-right">
            <div className="international-sidebar-head">
              <strong>Filter</strong>
              <button
                type="button"
                className="international-filter-toggle"
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
              </button>
              <button type="button" onClick={handleReset}>Reset</button>
            </div>

            <div className="international-sidebar-tools">
              <label className="international-list-search">
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search activities..."
                  autoComplete="off"
                />
                <Search size={15} />
              </label>

              <div className="international-sort-controls">
                <span>Sort By</span>
                {sortOptions.map((item) => (
                  <label className="international-sort-check" key={item}>
                    <input
                      type="checkbox"
                      checked={sort === item}
                      onChange={(event) => setSort(event.target.checked ? item : "")}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </div>

            {filterGroups.map((group) => (
              <div className="international-filter-group" key={group.title}>
                <div className="international-filter-title">
                  <span>{group.title}</span>
                </div>
                <div className="international-check-list">
                  {group.lines.map((line) => (
                    <label key={line}>
                      <input type="checkbox" />
                      <span>{line}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <section className="international-card-list">
            {activities.map((item) => (
              <article
                className="international-card mbt-animated-card"
                key={item.id || item.title}
                data-aos="fade-up"
              >
                <div className="international-card-image">
                  <img src={item.image} alt={item.title} />
                  <button type="button" aria-label="Save activity"><Heart size={15} /></button>
                  <div className="international-image-rating">
                    <Star size={15} />
                    <strong>{item.rating}</strong>
                    <span>({item.reviews} reviews)</span>
                  </div>
                </div>
                <div className="international-card-body">
                  <h2>{item.title}</h2>
                  <div className="international-meta">
                    <span><MapPin size={13} /> {item.location}</span>
                    <span><Clock3 size={13} /> {item.duration}</span>
                    <span className="international-rating"><Star size={13} /> {item.rating} ({item.reviews} reviews)</span>
                  </div>
                  <p>{item.desc}</p>
                  <div className="international-feature-row">
                    {["Curated Experience", "Local Guide", "Instant Confirmation", "Trusted & Safe"].map((feature) => (
                      <span key={feature}>
                        <ShieldCheck size={15} />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="international-card-price">
                  <strong>{formatPrice(item.price)}</strong>
                  <small>Per Person</small>
                  <del>{formatPrice(item.oldPrice)}</del>
                  <span><ShieldCheck size={13} /> Free Cancellation</span>
                  <button type="button" onClick={() => navigate("/activities/confirm", { state: { activity: item } })}>Book Now</button>
                </div>
              </article>
            ))}

            <div className="international-ad-card mbt-animated-card" data-aos="fade-up" data-aos-delay="160">
              <strong>MOROCCO</strong>
              <span>A Land of Endless Wonder.</span>
              <button type="button">Know More</button>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
